import type { BoxProps } from '@hover/blueprint';
import { Box, Button, Logo } from '@hover/blueprint';
import React, { useEffect } from 'react';

import { useLoginWithTopLevelOriginToken } from 'src/modules/homeownerApp/components/Embed/hooks/useLoginWithTopLevelOriginToken';
import {
  getUserSession,
  useHomeownerAppContext,
  useHomeownerTranslate,
} from 'src/modules/homeownerApp/hooks';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { useIsMobileSize } from 'src/modules/renderings/hooks';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';
import { useEmbedContext } from 'src/shared/providers/Embed';
import { buttonPressed } from 'src/shared/telemetry';

export interface EmbedFooterProps extends BoxProps {
  isVertical?: boolean;
  onLeadSheetToggle: () => void;
  onWelcomeSheetToggle?: () => void;
}

export const embedFooterStyles = {
  boxShadow: {
    base: '0px 0px 48px 0px #00000014',
    tablet: '0px 15px 44px 0px #0000001A',
  },
};

export const EmbedFooter = ({
  isVertical,
  onLeadSheetToggle,
  onWelcomeSheetToggle = () => {},
  ...props
}: EmbedFooterProps) => {
  const { trackingData, setCurrentMenu, loggedIn } = useHomeownerAppContext();
  const t = useHomeownerTranslate();
  const isMobile = useIsMobileSize();

  const { cta, ctaLink, userEmail, hasAccount } = useEmbedContext();
  const { affOrgPublicId } = useGlobalStorageContext();
  const { sessionId } = getUserSession() || {};

  const { authenticateWithTopLevelOriginToken, authenticationState } =
    useLoginWithTopLevelOriginToken();

  const redirectQueryParam = affOrgPublicId
    ? `?aff_org_id=${affOrgPublicId}`
    : '';

  let ctaLinkWithSession = ctaLink;
  const ctaText = hasAccount && !loggedIn ? t('embed.logIn') : cta;

  if (ctaLinkWithSession && sessionId) {
    if (ctaLinkWithSession.includes('?')) {
      ctaLinkWithSession += `&session_id=${sessionId}`;
    } else if (redirectQueryParam) {
      ctaLinkWithSession += redirectQueryParam;
      ctaLinkWithSession += `&session_id=${sessionId}`;
    }
  }

  const displayLightLogo = props.background || isMobile;
  const displayVerticalFooter = isMobile || isVertical;

  const onCtaButtonClick = () => {
    buttonPressed({
      ...trackingData,
      button_text: ctaText || 'Add your photo',
      track_id: 'embed-add-photo-primary',
    });

    if (userEmail && hasAccount && !loggedIn) {
      void authenticateWithTopLevelOriginToken();
      return;
    }

    if (userEmail) {
      setCurrentMenu(HomeownerAppMenuNames.UPLOAD_PHOTO);
      return;
    }
    onLeadSheetToggle();
  };

  useEffect(() => {
    const { authenticated, topLevelAccessed } = authenticationState;
    if (!authenticated && topLevelAccessed) {
      onWelcomeSheetToggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticationState]);

  return (
    <Box
      alignItems="center"
      background={{ base: 'neutral.900', tablet: 'neutral.0' }}
      flexDirection={displayVerticalFooter ? 'column' : 'row'}
      justifyContent={displayVerticalFooter ? 'center' : 'space-between'}
      sx={embedFooterStyles}
      {...props}
    >
      {!displayVerticalFooter && (
        <Logo
          height={200}
          marginTop={{ base: 400, tablet: 0 }}
          marginX={{ base: 400, tablet: 700 }}
        />
      )}
      {ctaText &&
        (ctaLinkWithSession ? (
          <Button
            as="a"
            color={displayLightLogo ? 'light' : 'primary'}
            href={ctaLinkWithSession}
            marginLeft={{
              base: 400,
              tablet: displayVerticalFooter ? 400 : 'auto',
            }}
            marginRight={{ base: 400, tablet: displayVerticalFooter ? 0 : 700 }}
            onClick={onCtaButtonClick}
            size={{ base: 'medium', tablet: 'large' }}
            target="_blank"
          >
            {ctaText}
          </Button>
        ) : (
          <Button
            color={displayLightLogo ? 'light' : 'primary'}
            marginLeft={{
              base: 400,
              tablet: displayVerticalFooter ? 400 : 'auto',
            }}
            marginRight={{ base: 400, tablet: displayVerticalFooter ? 0 : 700 }}
            onClick={onCtaButtonClick}
            size={{ base: 'medium', tablet: 'large' }}
          >
            {ctaText}
          </Button>
        ))}
      {displayVerticalFooter && (
        <Logo
          colorMode="dark"
          height={150}
          marginTop={{ base: 400, tablet: 500 }}
          paddingBottom={{ base: 200, tablet: 700 }}
        />
      )}
    </Box>
  );
};
